import PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import BottomElementContext from '../../Footer/service/BottomElementProvider/BottomElementContext';
import HeaderContext from './HeaderContext';
import StaticContext from './StaticContext';
import isSSR from '../../../utils/isSSR';
import useDealerFromCookie from './useDealerFromCookie';
import useDetectSticky from '../../ProgressBar/useDetectSticky';
import useEventHandler from '../../../utils/hooks/useEventHandler';
import useFetchDealer from '../../../core/apiHooks/useFetchDealer';
import { useLanguage } from '../../../utils/sitecoreContext';

const HeaderProvider = ({ children }) => {
  const [isOpenMenu, setIsOpenMenu] = useState(false);
  const [subMenuList, setSubMenuList] = useState({});
  const [activeMenu, setActiveMenu] = useState(null);
  const [firstOpen, setFirstOpen] = useState(true);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [headerBarMenuAnimationProps, setAnimationProps] = useState({ width: 0, left: 0 });
  const inventoryDealer = useSelector(state => state.dealer.dealerCode);

  const isExtended = inventoryDealer === '';

  const { isSticky } = useDetectSticky(isExtended);

  const primaryItemsRef = useRef({});
  const megaMenuRef = useRef({});
  const navBarRef = useRef({});

  const { headerHiddenOnScroll } = useContext(BottomElementContext);
  const { config, gtmTags, languageMenu, leftPrimaryItems, primaryItems, rightPrimaryItems, secondaryItems, siteLogo } =
    useContext(StaticContext);

  const language = useLanguage();

  // retrieve dealer code from cookie and send request for dealer data
  useDealerFromCookie();
  useFetchDealer();

  const handleAction = useEventHandler(data => {
    setFirstOpen(false);
    setActiveMenu(data);
    setSelectedMenu(data);
  });

  const handleHeaderNavScroll = () => {
    if (isSSR()) return undefined;
    if (headerHiddenOnScroll && activeMenu) {
      megaMenuRef.current?.[selectedMenu]?.current?.style?.setProperty(
        'top',
        config?.styles?.navItemPrimary?.megaMenuSimpleTopValue,
        'important',
      );
    }
  };

  const handleHeaderMegaMenuScroll = () => {
    if (isSSR()) return undefined;
    if (activeMenu) {
      const topDifference = navBarRef?.current?.getBoundingClientRect().top;
      const topValue = `${config.headerHeight + topDifference}px`;
      megaMenuRef.current?.[selectedMenu]?.current?.style?.setProperty('top', topValue, 'important');
    }
  };

  useEffect(() => {
    if (isSticky) {
      setActiveMenu(null);
      setSelectedMenu(null);
    }
  }, [isSticky]);

  const contextValue = useMemo(
    () => ({
      ...config,
      activeMenu,
      firstOpen,
      gtmTags,
      handleAction,
      handleHeaderMegaMenuScroll,
      handleHeaderNavScroll,
      headerBarMenuAnimationProps,
      headerHiddenOnScroll,
      isOpenMenu,
      languageMenu,
      leftPrimaryItems,
      megaMenuRef,
      navBarRef,
      primaryItems,
      primaryItemsRef,
      rightPrimaryItems,
      secondaryItems,
      selectedMenu,
      setActiveMenu,
      setAnimationProps,
      setFirstOpen,
      setIsOpenMenu,
      setSelectedMenu,
      setSubMenuList,
      siteLogo,
      subMenuList,
    }),
    [
      activeMenu,
      firstOpen,
      headerBarMenuAnimationProps,
      headerHiddenOnScroll,
      isOpenMenu,
      language,
      megaMenuRef,
      navBarRef,
      primaryItemsRef,
      selectedMenu,
      subMenuList,
      languageMenu,
    ],
  );

  return <HeaderContext.Provider value={contextValue}>{children}</HeaderContext.Provider>;
};

HeaderProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export default HeaderProvider;
